import { useCallback, useRef } from "react"
import { Platform } from "react-native"
import { useIsFocused } from "@react-navigation/native"
import { useComeForeground } from "src/hooks/useComeForeground"
import { getAppEnv } from "src/utils/env"

/**
 * バックグラウンド復帰時のリフレッシュ間隔（ミリ秒）
 * - DEV: 1分（テストしやすい）
 * - STG/PROD: 3分（コスト抑制）
 */
const getRefreshInterval = (): number => {
  const env = getAppEnv()
  if (env === "dev") {
    return 1 * 60 * 1000 // 1分
  }
  return 3 * 60 * 1000 // 3分
}

export type ForegroundRefreshOptions = {
  /**
   * 新しいデータがあるかチェックする関数
   * @returns 新しいデータがある場合はtrue
   */
  checkForNewData: () => Promise<boolean>
  /**
   * データをリフレッシュする関数（新しいデータがある場合に呼ばれる）
   */
  onRefresh: () => void
  /**
   * リフレッシュ対象の画面名（ログ用）
   */
  screenName: string
}

/**
 * バックグラウンド復帰時に条件付きでリフレッシュを行うフック
 *
 * 特徴:
 * - 最後のリフレッシュから一定時間経過した場合のみチェック
 * - 新しいデータがある場合のみキャッシュを破棄してリフレッシュ
 * - 新しいデータがない場合はキャッシュを維持（コスト抑制）
 * - Web版では無効（ブラウザタブ切り替えで頻繁に発火するため）
 */
export const useForegroundRefresh = (options: ForegroundRefreshOptions) => {
  const { checkForNewData, onRefresh, screenName } = options
  const isFocused = useIsFocused()
  const lastRefreshTime = useRef<number>(Date.now())
  const isChecking = useRef<boolean>(false)

  const handleForeground = useCallback(async () => {
    // Web版では無効
    if (Platform.OS === "web") return

    // 画面がフォーカスされていない場合はスキップ
    if (!isFocused) return

    // 既にチェック中の場合はスキップ
    if (isChecking.current) return

    const now = Date.now()
    const elapsed = now - lastRefreshTime.current
    const interval = getRefreshInterval()

    // 前回のリフレッシュから一定時間経過していない場合はスキップ
    if (elapsed < interval) {
      console.log(`[${screenName}] Foreground refresh skipped - interval not elapsed`, {
        elapsed: `${Math.round(elapsed / 1000)}s`,
        interval: `${interval / 1000}s`,
      })
      return
    }

    console.log(`[${screenName}] Checking for new data on foreground`)

    try {
      isChecking.current = true

      const hasNewData = await checkForNewData()

      if (hasNewData) {
        console.log(`[${screenName}] New data found - refreshing`)
        onRefresh()
        lastRefreshTime.current = now
      } else {
        console.log(`[${screenName}] No new data - keeping cache`)
        // 新しいデータがなくても最終チェック時刻は更新（頻繁なチェックを防ぐ）
        lastRefreshTime.current = now
      }
    } catch (error) {
      console.error(`[${screenName}] Error checking for new data`, error)
    } finally {
      isChecking.current = false
    }
  }, [isFocused, checkForNewData, onRefresh, screenName])

  useComeForeground(handleForeground, [handleForeground])

  // 手動リフレッシュ時に最終リフレッシュ時刻を更新
  const markRefreshed = useCallback(() => {
    lastRefreshTime.current = Date.now()
  }, [])

  return { markRefreshed }
}
